import { useEffect, useState } from 'react';
import { tenantOfApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Navigate, useRawLocation } from '.';
import { readTabTenant, readDefaultTenant } from '../utils/tabTenant';
import { tenantUrl, isGlobalPath } from '../utils/tenantPath';
import { allowedRestaurants, legacyLookupTarget, resolveLegacyTenant } from './tenantAccess';
import { TenantNotice } from './TenantGate';

// Bind each result to its request so an old path/user response cannot redirect
// a newer navigation. Rejections deliberately settle to the normal hints.
function useLegacyLookup(userId, type, id) {
  const [result, setResult] = useState(null);
  const key = userId && type ? `${userId}:${type}:${id}` : null;
  useEffect(() => {
    if (!key) return undefined;
    let active = true;
    tenantOfApi.lookup(type, id).then(
      response => { if (active) setResult({ key, rid: response.data.restaurant_id }); },
      () => { if (active) setResult({ key, rid: null }); },
    );
    return () => { active = false; };
  }, [key, type, id]);
  return { pending: key != null && result?.key !== key, rid: result?.key === key ? result.rid : null };
}

export default function LegacyRedirect() {
  const { user, realRole, restaurants, restaurantsLoaded, restaurantsError, refreshRestaurants } = useAuth();
  const location = useRawLocation();
  const params = new URLSearchParams(location.search);
  const queryAllowed = allowedRestaurants(realRole, user, restaurants)
    .some(r => String(r.id) === params.get('rid'));
  // An admin's ?rid= can only be recognised once the global list loads; a
  // lookup fired before then would be discarded.
  const awaitingAdminList = realRole === 'admin' && !restaurantsLoaded && params.has('rid');
  const target = !queryAllowed && !awaitingAdminList && legacyLookupTarget(location.pathname);
  const lookup = useLegacyLookup(user?.id, target?.type, target?.id);
  if (!user) return null;
  if (restaurantsError) return (
    <TenantNotice title="לא הצלחנו לטעון את רשימת המסעדות">
      <button className="btn btn-primary" style={{ minHeight: 44 }} onClick={refreshRestaurants}>נסו שוב</button>
    </TenantNotice>
  );
  if (realRole === 'admin' && !restaurantsLoaded) return <TenantNotice title="טוענים מסעדות"><span role="status">רק רגע…</span></TenantNotice>;
  if (lookup.pending) return <TenantNotice title="טוענים מסעדות"><span role="status">רק רגע…</span></TenantNotice>;
  const rid = resolveLegacyTenant({ realRole, user, restaurants,
    queryRid: params.get('rid'), lookupRid: lookup.rid, tabHint: readTabTenant(), lastHint: readDefaultTenant() });
  if (rid == null) return <TenantNotice title="עדיין לא שויכה לך מסעדה">פנו לצוות Peak לקבלת גישה למסעדה.</TenantNotice>;
  params.delete('rid');
  const search = params.toString();
  const path = location.pathname.startsWith('/r/') || isGlobalPath(location.pathname) ? '/suppliers' : location.pathname;
  return <Navigate replace state={location.state} to={tenantUrl(rid,
    `${path}${search ? `?${search}` : ''}${location.hash}`)} />;
}
